"use client";

import React from "react";
import type { Deal } from "@/lib/types/database/deals";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import { useDealWithLinkedCompanyFromDetail } from "@/contexts/DetailContextProvider";
import { AISummary } from "@/components/ai/ai-summary";
import { DEAL_TIMELINE_SUMMARY_OPTIONS } from "@/lib/domain/crm-entity-relationships";
import { DealCommunications } from "../DealCommunications";

export function DealTimelineDisplay({ item }: TabContentProps<Deal>) {
  const fromCtx = useDealWithLinkedCompanyFromDetail();
  const companyId =
    fromCtx?.linkedCompany.id ??
    (typeof item.company_id === "string" && item.company_id.length > 0 ? item.company_id : "");
  // `deals.company_name` is a nullable denormalized column — `?? undefined` so
  // a NULL reads as "not known" rather than being passed down as `null` (#2633).
  const companyDisplayName =
    fromCtx?.linkedCompany.displayName ?? item.company_name ?? undefined;
  const dealId = fromCtx?.dealId ?? item.id;

  return (
    <div className="space-y-4 md:space-y-6 min-h-[200px]">
      <AISummary
        entityType="deal"
        entityId={item.id}
        summaryType="timeline"
        options={{ ...DEAL_TIMELINE_SUMMARY_OPTIONS }}
        showMetadata={true}
        showRefreshButton={true}
        className="mb-0"
      />

      <div className="rounded-lg border border-border bg-muted/30 p-3 md:p-4">
        <h3 className="text-sm font-semibold text-foreground mb-2 md:mb-3">Deal milestones</h3>
        <div className="flex flex-col gap-2 sm:grid sm:grid-cols-4 sm:gap-3 text-sm">
          <div className="flex items-baseline justify-between gap-2 sm:block">
            <span className="text-xs text-muted-foreground shrink-0">Created</span>
            <p className="font-medium text-right sm:text-left">{new Date(item.created_at).toLocaleDateString()}</p>
          </div>
          <div className="flex items-baseline justify-between gap-2 sm:block">
            <span className="text-xs text-muted-foreground shrink-0">Updated</span>
            <p className="font-medium text-right sm:text-left">
              {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "—"}
            </p>
          </div>
          {item.won_at ? (
            <div className="flex items-baseline justify-between gap-2 sm:block">
              <span className="text-xs text-muted-foreground shrink-0">Won</span>
              <p className="font-medium text-right sm:text-left">{new Date(item.won_at).toLocaleDateString()}</p>
            </div>
          ) : null}
          {item.lost_at ? (
            <div className="flex items-baseline justify-between gap-2 sm:block">
              <span className="text-xs text-muted-foreground shrink-0">Lost</span>
              <p className="font-medium text-right sm:text-left">{new Date(item.lost_at).toLocaleDateString()}</p>
            </div>
          ) : null}
        </div>
      </div>

      {!companyId ? (
        <p className="text-sm text-muted-foreground">
          Link a company on this deal to load account communications and enrich the timeline.
        </p>
      ) : (
        <div className="space-y-3">
          <h3 className="text-base md:text-lg font-semibold text-foreground">Communications & activity</h3>
          <p className="text-sm text-muted-foreground">
            Activity for <span className="font-medium text-foreground">{companyDisplayName ?? "linked company"}</span> —
            same scope as the Communications tab.
          </p>
          <DealCommunications
            dealId={dealId}
            companyId={companyId}
            companyDisplayName={companyDisplayName}
          />
        </div>
      )}
    </div>
  );
}
